import React from "react";

import { faBookOpenCover, faCircleCheck, faCircleExclamation } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonLoading, Chip, Icon, Skeleton, Text, toast } from "@performyard/py-components";

import { useApplyTemplateLibraryEntry } from "../api/mutations";
import { useTemplateLibraryEntry } from "../api/queries";

import { TYPE_META } from "../type-meta";
import { PdfGuidePreview } from "./previews/PdfGuidePreview";
import { ReviewFormPreview } from "./previews/ReviewFormPreview";
import { ReviewWorkflowPreview } from "./previews/ReviewWorkflowPreview";
import { SurveyPreview } from "./previews/SurveyPreview";

type Props = {
	entryId: string | null;
	companyName: string;
	canApply: boolean;
};

export const TemplateLibraryDetailPanel = ({ entryId, companyName, canApply }: Props): React.ReactElement => {
	if (!entryId) return <EmptyDetail />;
	return <DetailContent entryId={entryId} companyName={companyName} canApply={canApply} />;
};

const EmptyDetail = (): React.ReactElement => (
	<div className="flex min-h-[480px] flex-col items-center justify-center gap-2 p-6 text-center">
		<div className="flex size-12 items-center justify-center rounded-full bg-primary-1">
			<Icon icon={faBookOpenCover} color="primary-7" size={24} />
		</div>
		<Text overrideElement="div" size="paragraph-1" weight="semi-bold" color="neutral-9">
			Pick something to preview
		</Text>
		<Text overrideElement="div" size="paragraph-3" weight="regular" color="neutral-7">
			Select an entry from the library on the left to see what&apos;s inside before applying it to your company.
		</Text>
	</div>
);

const DetailLoading = (): React.ReactElement => (
	<div className="space-y-4 p-6">
		<Skeleton className="h-3 w-32 rounded" />
		<Skeleton className="h-10 w-3/4 rounded" />
		<Skeleton className="h-4 w-11/12 rounded" />
		<Skeleton className="h-48 w-full rounded-md" />
	</div>
);

const DetailContent = ({
	entryId,
	companyName,
	canApply,
}: {
	entryId: string;
	companyName: string;
	canApply: boolean;
}): React.ReactElement => {
	const { data, isLoading } = useTemplateLibraryEntry(entryId);
	const apply = useApplyTemplateLibraryEntry();
	const [success, setSuccess] = React.useState(false);

	if (isLoading) return <DetailLoading />;

	if (!data) {
		return (
			<div className="flex min-h-[480px] flex-col items-center justify-center gap-2 p-6 text-center">
				<div className="flex size-12 items-center justify-center rounded-full bg-red-1">
					<Icon icon={faCircleExclamation} color="red-7" size={24} />
				</div>
				<Text overrideElement="div" size="paragraph-1" weight="semi-bold" color="neutral-9">
					Could not load preview
				</Text>
				<Text overrideElement="div" size="paragraph-3" weight="regular" color="neutral-7">
					Try selecting a different entry.
				</Text>
			</div>
		);
	}

	const meta = TYPE_META[data.type];

	const handleApply = (): void => {
		apply.mutate(entryId, {
			onSuccess: () => {
				// meta.short resolves to Form / Template / Survey / Document per object type.
				toast.success(`${meta.short} copied`);
				setSuccess(true);
				window.setTimeout(() => setSuccess(false), 3500);
			},
		});
	};

	return (
		<div className="flex h-full min-h-0 min-w-0 flex-col">
			<div className="min-h-0 flex-1 space-y-4 p-6 lg:overflow-y-auto">
				<div className="flex flex-wrap items-center gap-2">
					<Chip label={meta.label} icon={meta.icon} color="neutral" size={24} />
					<Text size="caption" weight="regular" color="neutral-7">
						v{data.version} · Updated {new Date(data.updated_at).toLocaleDateString()}
					</Text>
				</div>

				<Text overrideElement="h2" size="heading-5" weight="semi-bold" color="neutral-9">
					{data.name}
				</Text>

				{data.description && (
					<Text overrideElement="p" size="paragraph-3" weight="regular" color="neutral-7">
						{data.description}
					</Text>
				)}

				{data.tags.length > 0 && (
					<div className="flex flex-wrap items-center gap-2">
						{data.tags.map((tag) => (
							<Chip key={tag} label={tag} color="neutral" size={24} />
						))}
					</div>
				)}

				<section className="flex flex-col gap-4 rounded-md border border-neutral-3 bg-neutral-0 p-6">
					<Text overrideElement="h3" size="sub-heading" weight="semi-bold" color="neutral-9">
						What&apos;s inside
					</Text>
					{data.type === "review_workflow" && <ReviewWorkflowPreview payload={data.payload as never} />}
					{data.type === "review_form" && <ReviewFormPreview payload={data.payload as never} />}
					{data.type === "survey" && <SurveyPreview payload={data.payload as never} />}
					{data.type === "pdf_guide" && <PdfGuidePreview payload={data.payload as never} />}
				</section>
			</div>

			{canApply && (
				<div className="shrink-0 border-t border-neutral-2 bg-neutral-1 p-6">
					<div className="flex flex-wrap items-center justify-between gap-4">
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							{success ? (
								<span className="inline-flex items-center gap-2 font-semi-bold text-green-7">
									<Icon icon={faCircleCheck} color="green-6" size={16} />
									Applied to {companyName}. Refresh the destination page to see your new copy.
								</span>
							) : (
								<>
									Applying creates a new copy in <span className="font-semi-bold">{companyName}</span>
									. Your existing setup is untouched.
								</>
							)}
						</Text>
						<ButtonLoading onClick={handleApply} loading={apply.isPending}>
							Apply to {companyName}
						</ButtonLoading>
					</div>
				</div>
			)}
		</div>
	);
};

export default TemplateLibraryDetailPanel;
